
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { isFamilyOwner, isFamilyMember } from '@/utils/storage/family-permissions';
import { getUserFamilyId } from '@/utils/storage/helpers';
import { logger } from '@/utils/logger';

export type FamilyPermissions = {
  familyId: string | null;
  isOwner: boolean;
  isMember: boolean;
  isLoading: boolean;
  error: string | null;
};

export function useFamilyPermissions() {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<FamilyPermissions>({
    familyId: null,
    isOwner: false,
    isMember: false,
    isLoading: true,
    error: null
  });
  const [retryCount, setRetryCount] = useState(0);

  const checkFamilyPermissions = useCallback(async () => {
    if (!user?.id) {
      setPermissions({
        familyId: null,
        isOwner: false,
        isMember: false,
        isLoading: false,
        error: null
      });
      return;
    }

    try {
      logger.log('Checking family permissions for user:', user.id);
      setPermissions(prev => ({ ...prev, isLoading: true, error: null }));
      
      // First get the user's family ID
      const familyId = await getUserFamilyId();
      
      if (!familyId) {
        logger.log('User has no family');
        setPermissions({
          familyId: null,
          isOwner: false,
          isMember: false,
          isLoading: false,
          error: null
        });
        return;
      }
      
      // Determine if user is an owner and/or member
      const ownerStatus = await isFamilyOwner(familyId);
      const memberStatus = ownerStatus || await isFamilyMember(familyId);
      
      logger.log(`Family permissions: familyId=${familyId}, isOwner=${ownerStatus}, isMember=${memberStatus}`);
      
      setPermissions({
        familyId,
        isOwner: ownerStatus,
        isMember: memberStatus,
        isLoading: false,
        error: null
      });
      
    } catch (error: any) {
      logger.error('Error in checkFamilyPermissions:', error);
      setPermissions({
        familyId: null,
        isOwner: false,
        isMember: false,
        isLoading: false,
        error: error.message || 'An unexpected error occurred'
      });
    }
  }, [user?.id]);

  useEffect(() => {
    checkFamilyPermissions();
  }, [checkFamilyPermissions, retryCount]);

  const refreshPermissions = useCallback(() => {
    setRetryCount(prev => prev + 1);
  }, []);

  return {
    ...permissions,
    refreshPermissions
  };
}
